// The Meta Pixel in the app, under the cookie banner's one answer (#780).
//
// The shared parts — the loader, autoConfig off, the hashing, the cookies —
// are in marketing/metaPixel.js's header. This file is *when*, as
// src/lib/adConversions.js is for the Google Ads tag, under that file's
// rules: loaded only while `getConsent()` says 'granted' (no answer and no
// GPC, or "OK"); nothing fetched or set while it says 'denied'; a Decline
// turns it off in the same page (`consent revoke`, then `_fbp` / `_fbc`
// removed); a browser that starts denied and still holds them has them
// removed at startup; with no pixel id (the dev server, the emulator:
// src/lib/firebase.js) it never loads. Nothing here throws into a caller.
//
// ── What a PageView's address may carry ────────────────────────────────────
//
// The pixel sends the page's real address with every event — its `dl` is
// `location.href`, with no parameter to give it a clean one as the Google
// tags have. So an event waits until the address is clean (`addressIsClean`):
// no fragment, and a query of the ad's own parameters at most. What the app's
// addresses carry (App.jsx and the portal):
//
//   paths      route words, and opaque document ids: /students/<student id>
//              is the one route with an id in it. No name or email is ever
//              in a path.
//   queries    /login?mode=register (the sign-up deep link, dropped once
//              read) and /subscribed?session_id=cs_… (the Checkout session,
//              dropped once read, src/hooks/useThankYouPages.js); the ad's
//              gclid / utm_* / fbclid. Nothing else.
//   fragments  /portal/join#<invitation token> — a family's bearer token
//              for setting up its login. Never sent: the join page's view
//              waits for a clean address it does not get, and the next page
//              is counted instead.
//
// So a view of /login?mode=register is counted as /login a moment later, and
// the AddPaymentInfo on /subscribed is sent once the session id has been taken
// out of the address. A view whose address never comes clean is not counted.
//
// ── One view per screen ────────────────────────────────────────────────────
//
// Keyed on the pathname, as src/hooks/usePageViews.js is for analytics: a
// query or a fragment changing on the same screen is not a second view. A
// grant made on a screen counts the view of it, as analytics does.
//
// ── The conversions ────────────────────────────────────────────────────────
//
// src/lib/adConversions.js decides when a sign-up or a checkout is due — on
// /welcome after an account was made in this page, on /subscribed with a live
// Checkout session id — and hands it here at that moment (`metaConversionDue`),
// so Meta counts what Google Ads counts and nothing more. It is kept while
// that screen is shown, sent when the pixel is on and the address clean, and
// dropped the moment the screen is left or the visitor declines. StartTrial
// carries the sign-up's hashed email and uid as advanced matching, given by
// `metaNoteSignUp` when the account was made; the plain email is hashed there
// and not kept.
//
// The server sends the same conversions too, under the same ids, and Purchase
// besides (#781): `metaServerSignals` is what it is told of this browser.

import { getConsent, onConsentChange } from './consent';
import { CAMPAIGN_TAGS, CLICK_IDS } from '../../marketing/adTag.js';
import {
  META_EVENTS, clearMetaStorage, freshEventId, isMetaPixelId, startMetaPixel, trackMeta, updateMetaConsent,
} from '../../marketing/metaPixel.js';
import { addPaymentInfoEventId, hashedMatching, startTrialEventId } from '../domain/metaEvents.js';

// The ad's own parameters: Google's (marketing/adTag.js) and Meta's click id.
const AD_PARAMS = new Set([...CLICK_IDS, ...CAMPAIGN_TAGS, 'fbclid']);

/**
 * Whether an address may be sent to Meta as it is: no fragment, and nothing
 * in the query but the ad's parameters.
 */
export function addressIsClean(search, hash) {
  if (hash && hash !== '#') return false;
  try {
    for (const key of new URLSearchParams(String(search || '')).keys()) {
      if (!AD_PARAMS.has(key)) return false;
    }
    return true;
  } catch {
    return false;
  }
}

let wiring = null;
let fbq = null;
// 'off' | 'on'
let phase = 'off';
let screen = null; // { pathname, clean }
let viewed = null; // the pathname whose view was counted
let due = null; // { kind, path, sessionId? }
// { matching: Promise<{ em?, external_id? }>, eventId: Promise<string|null> }:
// hashes only, so the raw uid and email are not kept past `metaNoteSignUp`.
let signUp = null;
let stopListening = null;

/**
 * Wire the pixel to the consent answer. Called once, from src/main.jsx.
 * `pixelId` is Meta's id, or nothing (nothing ever loads).
 * @returns {boolean} whether the pixel is wired at all
 */
export function installMetaPixel({ pixelId } = {}) {
  try {
    if (getConsent() !== 'granted') clearMetaStorage();
  } catch {
    // No document: nothing to clear.
  }
  if (wiring || !isMetaPixelId(pixelId)) return false;
  wiring = { id: pixelId };
  stopListening = onConsentChange((choice) => {
    if (choice === 'granted') start();
    else withdraw();
  });
  if (getConsent() === 'granted') start();
  return true;
}

/**
 * The router's location, on every change (src/hooks/usePageViews.js). Counts
 * the screen's view once its address is clean, drops a conversion whose
 * screen was left, and sends one that is due.
 */
export function metaScreen(pathname, search, hash) {
  try {
    const path = String(pathname || '/').replace(/(.)\/+$/, '$1');
    if (screen?.pathname !== path) viewed = null;
    screen = { pathname: path, clean: addressIsClean(search, hash) };
    if (due && due.path !== path) due = null;
    flush();
  } catch {
    // Never in the screen's way.
  }
}

/** A new account was made in this page: hash what Meta may match it by, and keep only that. */
export function metaNoteSignUp({ uid = null, email = null } = {}) {
  try {
    if (typeof uid !== 'string' || !uid) {
      signUp = null;
      return;
    }
    signUp = { matching: hashedMatching({ uid, email }), eventId: startTrialEventId(uid) };
  } catch {
    signUp = null;
  }
}

/**
 * A conversion is due on the screen being shown, from src/lib/adConversions.js
 * `reachScreen`: `{ kind: 'signUp', path }` or `{ kind: 'paid', path, sessionId }`.
 */
export function metaConversionDue(conversion) {
  try {
    if (conversion?.kind === 'signUp' && !signUp) return;
    if (conversion?.kind === 'paid' && !conversion.sessionId) return;
    due = { ...conversion };
    flush();
  } catch {
    // Never in the screen's way.
  }
}

/**
 * The teacher is opening Stripe Checkout (src/components/settings/MembershipPanel.jsx).
 * Sends InitiateCheckout and returns its event id, for `createCheckoutSession`
 * to carry; returns null when nothing was sent — declined, not wired, or an
 * address that is not clean — so the server has no id to send an event of its
 * own for a visitor the browser did not count.
 */
export function metaInitiateCheckout() {
  try {
    if (phase !== 'on' || !fbq || !screen?.clean || getConsent() !== 'granted') return null;
    const eventId = freshEventId('initiatecheckout');
    trackMeta(fbq, META_EVENTS.initiateCheckout, {}, eventId);
    return eventId;
  } catch {
    return null;
  }
}

// ── What the server is told (#781) ────────────────────────────────────────
//
// The Conversions API sends the same conversions from the server
// (functions/billing/metaCapi.js), and may only when the answer in force here
// was a grant. So the sign-up's call and `createCheckoutSession` carry that
// answer — `getConsent()`, the one opt-out answer, whether or not the pixel is
// wired in this build — and, beside a grant, the pixel's `_fbp` and `_fbc`
// exactly as it set them, each only when present. Nothing else: the server
// takes the address and browser from the request itself.

const FB_COOKIE_VALUE = /^fb\.[0-9]\.[0-9]{9,16}\.[A-Za-z0-9_.-]{1,500}$/;

/** The pixel's `_fbp` and `_fbc` values in a `document.cookie` string, where they are well formed. */
export function metaCookieValues(cookieHeader) {
  const out = {};
  for (const part of String(cookieHeader || '').split(';')) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    const name = part.slice(0, eq).trim();
    if (name !== '_fbp' && name !== '_fbc') continue;
    let value = part.slice(eq + 1).trim();
    try {
      value = decodeURIComponent(value);
    } catch {
      // Left as it was; the shape check decides.
    }
    if (FB_COOKIE_VALUE.test(value)) out[name.slice(1)] = value;
  }
  return out;
}

/**
 * `{ metaConsent, fbp?, fbc? }` for a callable's request: the answer in force,
 * and the pixel's cookies only beside a grant. Empty if the answer cannot be
 * read. Never throws.
 */
export function metaServerSignals({ doc = typeof document === 'undefined' ? null : document } = {}) {
  try {
    if (getConsent() !== 'granted') return { metaConsent: 'denied' };
    return { metaConsent: 'granted', ...metaCookieValues(doc?.cookie) };
  } catch {
    return {};
  }
}

/** Forget everything, for tests. */
export function resetMetaPixelForTests() {
  stopListening?.();
  stopListening = null;
  wiring = null;
  fbq = null;
  phase = 'off';
  screen = null;
  viewed = null;
  due = null;
  signUp = null;
}

/** Where the module is, for tests. */
export const metaPixelPhase = () => phase;

function flush() {
  if (phase !== 'on' || !fbq || !screen?.clean) return;
  if (viewed !== screen.pathname) {
    viewed = screen.pathname;
    trackMeta(fbq, META_EVENTS.pageView, {}, freshEventId('pageview'));
  }
  if (due && due.path === screen.pathname) sendDue();
}

function sendDue() {
  const conversion = due;
  due = null;
  if (conversion.kind === 'paid') {
    // A card on file, nothing charged in the trial: no value, no currency.
    trackMeta(fbq, META_EVENTS.addPaymentInfo, {}, addPaymentInfoEventId(conversion.sessionId));
    return;
  }
  if (conversion.kind !== 'signUp' || !signUp) return;
  const { matching, eventId } = signUp;
  Promise.all([matching, eventId]).then(([fields, id]) => {
    // Declined, or the screen left, while the hashes were being made; or no
    // hash could be made, and an id without one would carry the raw uid.
    if (phase !== 'on' || !fbq || screen?.pathname !== conversion.path || !screen.clean || !id) return;
    // Manual advanced matching is given to `init`; given again, it is added
    // to the pixel already running, and goes with this event and the ones
    // after it. (Reasoned from Meta's documentation of manual advanced
    // matching, not observed: the Pixel Helper's "Advanced Matching
    // parameters" on StartTrial is the check.)
    if (fields && Object.keys(fields).length) fbq('init', wiring.id, { ...fields });
    trackMeta(fbq, META_EVENTS.startTrial, {}, id);
  }, () => {});
}

function start() {
  if (!wiring) return;
  try {
    if (fbq) {
      // Allowed again after a Decline in this same page.
      updateMetaConsent(fbq, 'granted');
    } else {
      fbq = startMetaPixel({ id: wiring.id });
    }
    phase = 'on';
    flush();
  } catch {
    // Blocked, or no document: no pixel in this page.
    phase = 'off';
  }
}

function withdraw() {
  due = null;
  phase = 'off';
  // Allowed again on this screen, its view is counted again, as analytics does.
  viewed = null;
  try {
    if (fbq) updateMetaConsent(fbq, 'denied');
  } catch {
    // The pixel was never reachable.
  }
  clearMetaStorage();
}
